import React, { useEffect, useState } from 'react';
import { fetchTargetClasses, uploadStudy, UploadProgress } from '../api';
import { TargetClass } from '../types';
import { Upload, X, Lock, Loader2 } from 'lucide-react';

/**
 * Every IANA zone the browser knows, for the wear-location picker below. `supportedValuesOf` is
 * the only way to get the real list without shipping one; where it is missing the field falls back
 * to the browser's own zone alone, which still beats the hardcoded constant this replaces.
 */
function timeZoneOptions(): string[] {
  try {
    const supported = (Intl as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
    if (supported) return supported('timeZone');
  } catch {
    /* fall through */
  }
  return [];
}

/** The browser's own zone — a guess at where the device was worn, not an answer. See the field. */
function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

//: Computed once at module load: the list is several hundred entries and never changes, so
//: rebuilding it on every render of the modal would be pure waste. The browser's own zone is
//: forced into the list — a <select> whose value is absent from its options silently displays the
//: wrong one, which on this particular field means silently stamping the wrong wall clock.
const TZ_OPTIONS: string[] = (() => {
  const all = timeZoneOptions();
  if (all.length === 0) return [];
  const own = browserTimeZone();
  return all.includes(own) ? all : [own, ...all];
})();

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the new study's id once ingest completes, so the caller can open it directly. */
  onUploaded: (studyId: string) => void;
}

export const UploadModal: React.FC<UploadModalProps> = ({ isOpen, onClose, onUploaded }) => {
  const [file, setFile] = useState<File | null>(null);
  // Empty by default, never a placeholder identity: a pre-filled name is submitted by anyone who
  // does not notice it, and then sits on a clinical record as if it were the patient's.
  const [patientName, setPatientName] = useState<string>('');
  const [patientRef, setPatientRef] = useState<string>('');
  // Defaulted to the browser's zone and shown as an editable field, rather than the hardcoded
  // 'Asia/Kolkata' every upload used to be stamped with silently (§13.6 I6). The browser's zone is
  // the *uploader's*, which is only a guess at the patient's — a study worn in London and uploaded
  // from Mumbai is exactly the case that goes wrong — so the point of the field is that it is
  // visible and correctable, not that the default is clever.
  const [studyTimezone, setStudyTimezone] = useState<string>(browserTimeZone());
  // Findings to target (DESIGN.md D71, §6.12). Fetched rather than hardcoded so the picker
  // cannot offer a class no detector emits, or miss one it does.
  const [catalogue, setCatalogue] = useState<TargetClass[]>([]);
  const [targets, setTargets] = useState<Set<string>>(new Set());
  const [showTargets, setShowTargets] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || catalogue.length > 0) return;
    // Everything on by default: the clinical default is to look for everything the engine can
    // report, and narrowing it is a deliberate act (§15 Q40). A picker that started empty would
    // make "nobody touched this" mean "look for nothing".
    fetchTargetClasses()
      .then((classes) => {
        setCatalogue(classes);
        setTargets(new Set(classes.map((c) => c.rhythm_class)));
      })
      .catch(() => {
        /* The picker is optional: an upload with no selection records "the default applied",
           which is exactly what a failed fetch should leave behind. */
      });
  }, [isOpen, catalogue.length]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Please select an EDF / EDF+ recording file.');
      return;
    }
    if (!patientName.trim() || !patientRef.trim()) {
      setError('Enter the patient name and patient reference.');
      return;
    }

    setIsUploading(true);
    setProgress(null);
    setError(null);
    try {
      // Registration finishes here — the file itself is still uploading to storage when this
      // resolves for a large recording is not true any more (§13.6 I1/I2): `uploadStudy` now
      // waits for the direct-to-storage transfer to finish before registering the study, so by
      // the time this returns the study exists as INGESTING and a worker has already picked up
      // parsing. `onUploaded` opens straight into that state; the workspace shows it explicitly.
      const study = await uploadStudy({
        file,
        patientRef: patientRef.trim(),
        patientName: patientName.trim(),
        studyTimezone,
        // Sent only when the catalogue actually loaded; otherwise the study records that the
        // default applied, rather than recording a choice nobody made.
        targetClasses: catalogue.length > 0 ? Array.from(targets) : undefined,
        onProgress: setProgress,
      });
      setIsUploading(false);
      setFile(null);
      setPatientName('');
      setPatientRef('');
      setProgress(null);
      onUploaded(study.id);
    } catch (err: any) {
      setIsUploading(false);
      setError(err.message || 'Failed to upload EDF study');
    }
  };

  const progressPercent = progress && progress.total > 0 ? Math.round((progress.loaded / progress.total) * 100) : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-4">
      <div className="glass-panel w-full max-w-lg p-6 rounded-2xl space-y-5 border border-slate-800 shadow-2xl relative animate-in fade-in zoom-in duration-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2.5">
            <div className="h-9 w-9 rounded-xl bg-cyan-950 border border-cyan-800 flex items-center justify-center text-cyan-400">
              <Upload className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">Upload EDF Study</h3>
              {/* "Under a minute" was measured on a short synthetic file and was never true of a
                  real recording: a 101-hour study takes minutes to upload and minutes more to
                  analyse (§13.6 R8). A promise the product cannot keep makes a working system
                  look broken, and the progress view already reports the real state. */}
              <p className="text-xs text-slate-400">Ingest a continuous 1–7 day EDF/EDF+ recording. Analysis starts automatically; a multi-day study takes several minutes, and the study page shows its progress.</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="bg-rose-950/80 border border-rose-800 text-rose-300 text-xs p-3 rounded-xl space-y-1">
            <p className="font-semibold">Upload refused</p>
            <p>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          <div>
            <label className="block text-slate-300 font-semibold mb-1">Patient Name</label>
            <input
              type="text"
              value={patientName}
              onChange={(e) => setPatientName(e.target.value)}
              className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3.5 py-2.5 text-slate-200 focus:outline-none focus:border-cyan-500"
              required
            />
          </div>

          <div>
            <label className="block text-slate-300 font-semibold mb-1">Patient MRN / ID</label>
            <input
              type="text"
              value={patientRef}
              onChange={(e) => setPatientRef(e.target.value)}
              className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3.5 py-2.5 text-slate-200 focus:outline-none focus:border-cyan-500"
              required
            />
          </div>

          <div>
            <label className="block text-slate-300 font-semibold mb-1">EDF / EDF+ File</label>
            <input
              type="file"
              accept=".edf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2 text-slate-400 file:mr-4 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-cyan-950 file:text-cyan-400 hover:file:bg-cyan-900"
              required
            />
          </div>

          <div>
            <label className="block text-slate-300 font-semibold mb-1">
              Recording timezone
            </label>
            {/* An EDF header's start time carries no zone at all, so this is the only thing that
                says which wall clock it was written against. Get it wrong and every timestamp on
                the report — and every episode time a clinician reads — is offset (§13.6 I6). */}
            {TZ_OPTIONS.length > 0 ? (
              <select
                value={studyTimezone}
                onChange={(e) => setStudyTimezone(e.target.value)}
                className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3.5 py-2.5 text-slate-200 focus:outline-none focus:border-cyan-500"
                required
              >
                {TZ_OPTIONS.map((zone) => (
                  <option key={zone} value={zone}>{zone}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={studyTimezone}
                onChange={(e) => setStudyTimezone(e.target.value)}
                placeholder="Asia/Kolkata"
                className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3.5 py-2.5 text-slate-200 focus:outline-none focus:border-cyan-500"
                required
              />
            )}
            <p className="mt-1 text-[11px] text-slate-500">
              Where the patient wore the device — not where you are. Defaulted to this browser's
              timezone; change it if the recording was made elsewhere.
            </p>
          </div>

          {catalogue.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setShowTargets((v) => !v)}
                className="flex w-full items-center justify-between text-left"
              >
                <span className="font-semibold text-slate-300">
                  Findings to target
                  <span className="ml-2 font-normal text-slate-500">
                    {targets.size} of {catalogue.length} selected
                  </span>
                </span>
                <span className="text-[11px] text-cyan-400">{showTargets ? 'Hide' : 'Change'}</span>
              </button>
              <p className="mt-1 text-[11px] text-slate-500">
                What this study is looking for. Narrowing it focuses the review queue and the
                report; life-threatening findings are always assessed and cannot be switched off.
              </p>
              {showTargets && (
                <div className="mt-2 grid grid-cols-1 gap-1 rounded-xl border border-slate-800 bg-slate-900/60 p-2 sm:grid-cols-2">
                  {catalogue.map((entry) => {
                    const checked = entry.locked || targets.has(entry.rhythm_class);
                    return (
                      <label
                        key={entry.rhythm_class}
                        title={entry.reason ?? undefined}
                        className={`flex items-center gap-2 rounded-lg px-2 py-1 ${
                          entry.locked ? 'text-slate-400' : 'cursor-pointer text-slate-200 hover:bg-slate-800/60'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={entry.locked}
                          onChange={(e) =>
                            setTargets((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(entry.rhythm_class);
                              else next.delete(entry.rhythm_class);
                              return next;
                            })
                          }
                          className="h-3.5 w-3.5 accent-cyan-500"
                        />
                        <span className="flex items-center gap-1">
                          {entry.label}
                          {entry.locked && <Lock className="h-3 w-3 text-slate-500" />}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {isUploading && (
            <div className="space-y-1">
              <div className="h-1.5 w-full rounded-full bg-slate-900 overflow-hidden">
                <div
                  className="h-full bg-cyan-500 transition-all"
                  style={{ width: `${progressPercent ?? 0}%` }}
                />
              </div>
              <p className="text-[11px] text-slate-500">
                {progressPercent !== null ? `Uploading… ${progressPercent}%` : 'Uploading…'}
              </p>
            </div>
          )}

          <div className="pt-2 flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl bg-slate-900 text-slate-300 hover:text-white font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUploading}
              className="flex items-center space-x-2 bg-cyan-600 hover:bg-cyan-500 text-white font-medium px-5 py-2.5 rounded-xl shadow-lg shadow-cyan-600/30 transition-all disabled:opacity-50"
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{progressPercent !== null ? `Uploading ${progressPercent}%` : 'Uploading...'}</span>
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  <span>Start Ingest</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
